import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Pill,
  ArrowLeft,
  Save,
  ScanBarcode,
  Stethoscope,
  FileText,
  CheckCircle2,
  Layers,
  Plus,
  Trash2,
  RefreshCw,
  Info,
} from 'lucide-react';
import { clsx } from 'clsx';
import toast from 'react-hot-toast';
import api from '../config/api';
import SearchableSelect from '../components/SearchableSelect';

function InputField({ label, value, onChange, placeholder, type = 'text', required, disabled = false, prefix, suffix, icon: Icon }) {
  return (
    <div>
      <label className="mb-1.5 block text-[11px] font-bold uppercase tracking-wider text-gray-500">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <div className="relative">
        {Icon && <Icon className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />}
        {prefix && !Icon && <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-sm text-gray-400">{prefix}</span>}
        <input
          type={type}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          required={required}
          disabled={disabled}
          className={clsx(
            'w-full rounded-xl border border-gray-200 bg-gray-50 py-3 text-sm text-gray-900 placeholder-gray-400 transition-all focus:border-emerald-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500/20',
            (prefix || Icon) ? 'pl-10 pr-4' : 'px-4',
            suffix ? 'pr-16' : ''
          )}
        />
        {suffix && <span className="absolute right-3.5 top-1/2 -translate-y-1/2 text-xs font-semibold text-gray-400">{suffix}</span>}
      </div>
    </div>
  );
}

function SelectField({ label, value, onChange, options, placeholder, searchPlaceholder, required, creatable = false }) {
  return (
    <div>
      <label className="mb-1.5 block text-[11px] font-bold uppercase tracking-wider text-gray-500">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <SearchableSelect
        value={value}
        onChange={onChange}
        options={options}
        placeholder={placeholder || `Select ${label}...`}
        searchPlaceholder={searchPlaceholder || `Search ${label.toLowerCase()}...`}
        creatable={creatable}
        size="lg"
      />
    </div>
  );
}

function SectionCard({ title, icon: Icon, children }) {
  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
      <div className="mb-5 flex items-center gap-2">
        {Icon && <Icon className="h-4 w-4 text-emerald-500" />}
        <h2 className="text-sm font-bold uppercase tracking-wider text-gray-700">{title}</h2>
      </div>
      {children}
    </div>
  );
}

const DEFAULT_FORMULATIONS = [
  'Tablet',
  'Capsule',
  'Syrup',
  'Suspension',
  'Drops',
  'Inhaler',
  'Injection',
  'Cream',
  'Ointment',
  'Gel',
  'Powder',
  'Solution',
  'Sachet',
  'Suppository',
  'Lotion',
  'Spray',
  'Patch',
  'Emulsion',
  'Elixir',
];

const COMMON_UNIT_NAMES = [
  'Box',
  'Pack',
  'Strip',
  'Tablet',
  'Capsule',
  'Bottle',
  'Syrup',
  'Piece',
  'Tube',
  'Sachet',
  'Ampoule',
  'Vial',
  'Drop',
  'Inhaler',
  'Carton',
  'Blister',
];

// These describe how a product is packed, not what it medically is. They
// belong only in the unit hierarchy and must never become formulations.
const PACKAGING_NAMES = new Set([
  'box', 'pack', 'strip', 'piece', 'bottle', 'tube', 'carton', 'blister',
  'sachet', 'vial', 'ampoule', 'bar', 'can', 'bag', 'jar', 'packet',
]);

const isPackagingName = (value) => PACKAGING_NAMES.has(String(value || '').trim().toLowerCase());

// Stock is stored in the smallest unit so that partial strips/packs are never
// lost. Show its packaging equivalent on the edit form, rather than making a
// base-unit quantity look like the product's configured selling unit changed.
function formatStockBreakdown(baseQuantity, units) {
  const quantity = Math.max(0, parseInt(baseQuantity, 10) || 0);
  if (!units?.length) return `${quantity} units`;

  let remaining = quantity;
  const parts = units.map((unit, index) => {
    const factor = Math.max(1, parseInt(unit.conversion_factor, 10) || units
      .slice(index, -1)
      .reduce((product, parent) => product * Math.max(1, parseInt(parent.child_count, 10) || 1), 1));
    const count = Math.floor(remaining / factor);
    remaining %= factor;
    return count > 0 ? `${count} ${unit.unit_name || 'units'}` : null;
  });

  const nonZeroParts = parts.filter(Boolean);
  return `${quantity} ${units[units.length - 1]?.unit_name || 'units'} = ${nonZeroParts.join(' + ') || `0 ${units[units.length - 1]?.unit_name || 'units'}`}`;
}

export default function NewMedicine() {
  const navigate = useNavigate();
  const { id: medicineId } = useParams();
  const isEdit = Boolean(medicineId);
  const [categories, setCategories] = useState([]);
  const [saved, setSaved] = useState(false);
  const [loadingMedicine, setLoadingMedicine] = useState(isEdit);
  const [editingBatch, setEditingBatch] = useState(null);
  const [formulations, setFormulations] = useState(() => {
    try {
      const stored = JSON.parse(localStorage.getItem('gaza_custom_formulations') || '[]');
      const validStored = stored.filter((name) => !isPackagingName(name));
      // Remove stale packaging labels saved by older versions of the form.
      if (validStored.length !== stored.length) {
        localStorage.setItem('gaza_custom_formulations', JSON.stringify(validStored));
      }
      return Array.from(new Set([...DEFAULT_FORMULATIONS, ...validStored]));
    } catch {
      return DEFAULT_FORMULATIONS;
    }
  });

  const [form, setForm] = useState({
    barcode: '',
    sku: '',
    name: '',
    generic_name: '',
    manufacturer: '',
    category_id: '',
    new_category: '',
    formulation: 'Tablet',
    strength: '',
    packSize: '',
    reorder_level: '10',
    quantity: '',
    mf_date: '',
    expiry_date: '',
    description: '',
  });

  // Dynamic Multi-Level Unit Hierarchy (clean initial state without dummy prices)
  const [units, setUnits] = useState([
    { unit_name: 'Box', child_count: '', cost_price: '', selling_price: '' },
    { unit_name: 'Strip', child_count: '', cost_price: '', selling_price: '' },
    { unit_name: 'Tablet', child_count: 1, cost_price: '', selling_price: '' },
  ]);

  const topConversionFactor = units.slice(0, -1).reduce(
    (factor, unit) => factor * Math.max(1, parseInt(unit.child_count, 10) || 1),
    1
  );
  const stockBreakdown = isEdit
    ? formatStockBreakdown(
      editingBatch
        ? (Math.max(0, parseInt(form.quantity, 10) || 0) * editingBatch.conversionFactor)
          + (editingBatch.remainderBase || 0)
        : Math.max(0, parseInt(form.quantity, 10) || 0) * topConversionFactor,
      units
    )
    : '';

  useEffect(() => {
    loadCategories();
    loadFormulations();
    if (isEdit) loadMedicine();
  }, [medicineId]);

  const loadMedicine = async () => {
    try {
      const response = await api.get(`/medicines/${medicineId}`);
      const medicine = response?.medicine || response;
      if (!medicine?.id) throw new Error('Medicine not found');
      setForm({
        barcode: medicine.barcode || '',
        sku: medicine.sku || '',
        name: medicine.name || '',
        generic_name: medicine.generic_name || '',
        manufacturer: medicine.manufacturer || '',
        category_id: medicine.category_id ? String(medicine.category_id) : '',
        new_category: '',
        formulation: medicine.formulation || 'Tablet',
        strength: medicine.strength || '',
        packSize: medicine.pack_size || '',
        reorder_level: String(medicine.reorder_level ?? 10),
        quantity: '',
        mf_date: '',
        expiry_date: '',
        description: medicine.description || '',
      });
      const loadedUnits = Array.isArray(medicine.units) && medicine.units.length
        ? medicine.units.map((unit) => ({
            unit_name: unit.unit_name || '',
            child_count: unit.child_count ?? 1,
            cost_price: unit.cost_price ?? '',
            selling_price: unit.selling_price ?? '',
          }))
        : [{ unit_name: medicine.unit || 'pcs', child_count: 1, cost_price: '', selling_price: medicine.selling_price || '' }];
      setUnits(loadedUnits);
      const batch = Array.isArray(medicine.batches) && medicine.batches.length ? medicine.batches[0] : null;
      if (batch) {
        const computedUnits = loadedUnits;
        let topConversion = 1;
        if (computedUnits.length > 1) {
          for (let i = 0; i < computedUnits.length - 1; i++) {
            topConversion *= Math.max(1, parseInt(computedUnits[i].child_count, 10) || 1);
          }
        }
        if (topConversion <= 1) {
          topConversion = Math.max(1, parseInt(medicine.units?.[0]?.conversion_factor, 10) || 1);
        }
        const baseQuantity = Math.max(0, parseInt(batch.quantity, 10) || 0);
        setEditingBatch({
          id: batch.id,
          conversionFactor: topConversion,
          remainderBase: baseQuantity % topConversion,
          batchNumber: batch.batch_number,
          baseUnitName: (computedUnits[computedUnits.length - 1]?.unit_name || '').trim(),
        });
        setForm((prev) => ({
          ...prev,
          // Staff enter complete top-level packs (Box, Pack, etc.). Loose
          // stock remains preserved separately when this batch is saved.
          quantity: String(Math.floor(baseQuantity / topConversion)),
          mf_date: batch.mf_date || '',
          expiry_date: batch.expiry_date || '',
        }));
      }
    } catch (err) {
      toast.error(err.message || 'Failed to load medicine');
      navigate('/medicines', { replace: true });
    } finally {
      setLoadingMedicine(false);
    }
  };

  const loadCategories = async () => {
    try {
      const cats = await api.get('/categories');
      setCategories(Array.isArray(cats) ? cats : (cats?.categories || []));
    } catch {
      // ignore
    }
  };

  const loadFormulations = async () => {
    try {
      const res = await api.get('/formulations');
      const list = Array.isArray(res) ? res : (res?.formulations || []);
      if (list.length > 0) {
        const names = list
          .map((f) => (typeof f === 'string' ? f : f.name))
          .filter((name) => name && !isPackagingName(name));
        setFormulations(Array.from(new Set([...DEFAULT_FORMULATIONS, ...names])));
      }
    } catch {
      // ignore
    }
  };

  const updateForm = (field) => (e) => {
    const val = e && typeof e === 'object' && 'target' in e ? e.target.value : e;
    setForm((prev) => ({ ...prev, [field]: val }));
  };

  const handleFormulationChange = async (val) => {
    const trimmedVal = typeof val === 'string' ? val.trim() : val;
    if (!trimmedVal) return;
    if (isPackagingName(trimmedVal)) {
      toast.error('Box, Strip, Bottle, and other packaging names belong in Unit Conversion, not Formulation.');
      return;
    }

    setForm((prev) => ({ ...prev, formulation: trimmedVal }));

    // If new custom formulation, persist it in database and state
    if (!formulations.includes(trimmedVal)) {
      setFormulations((prev) => Array.from(new Set([...prev, trimmedVal])));
      try {
        await api.post('/formulations', { name: trimmedVal });
      } catch {
        // ignore
      }
      try {
        const stored = JSON.parse(localStorage.getItem('gaza_custom_formulations') || '[]');
        localStorage.setItem('gaza_custom_formulations', JSON.stringify(Array.from(new Set([...stored, trimmedVal]))));
      } catch {
        // ignore
      }
    }

    // Intelligently sync base unit name with selected formulation if still using standard defaults
    setUnits((prev) => {
      if (prev.length === 0) return prev;
      const copy = [...prev];
      const lastIndex = copy.length - 1;
      const currentBaseUnit = copy[lastIndex].unit_name;

      const autoMap = {
        Capsule: 'Capsule',
        Tablet: 'Tablet',
        Syrup: 'Bottle',
        Suspension: 'Bottle',
        Drops: 'Drop',
        Inhaler: 'Inhaler',
        Injection: 'Ampoule',
        Cream: 'Tube',
        Ointment: 'Tube',
        Gel: 'Tube',
        Powder: 'Sachet',
        Solution: 'Bottle',
        Sachet: 'Sachet',
        Suppository: 'Piece',
        Lotion: 'Bottle',
        Spray: 'Bottle',
        Patch: 'Patch',
        Emulsion: 'Bottle',
        Elixir: 'Bottle',
      };

      const typicalUnits = ['Tablet', 'Capsule', 'Bottle', 'Piece', 'Ampoule', 'Tube', 'Drop', 'Sachet', 'Inhaler', 'Patch'];
      if (autoMap[trimmedVal] && typicalUnits.includes(currentBaseUnit)) {
        copy[lastIndex] = { ...copy[lastIndex], unit_name: autoMap[trimmedVal] };
      }
      return copy;
    });
  };

  // Helper to re-calculate child costs and selling prices down the hierarchy if top prices are filled
  const recalculatePrices = (updatedUnits, changedIndex = 0) => {
    const list = [...updatedUnits];
    for (let i = changedIndex; i < list.length - 1; i++) {
      const parent = list[i];
      const child = list[i + 1];
      const ratio = parseInt(parent.child_count, 10);

      if (!ratio || ratio <= 0) continue;

      const parentCost = parseFloat(parent.cost_price);
      const parentSell = parseFloat(parent.selling_price);

      if (!isNaN(parentCost) && parentCost > 0) {
        child.cost_price = (parentCost / ratio).toFixed(2);
      }
      if (!isNaN(parentSell) && parentSell > 0) {
        child.selling_price = (parentSell / ratio).toFixed(2);
      }
    }
    return list;
  };

  const handleUnitChange = (index, field, value) => {
    setUnits((prev) => {
      const copy = prev.map((u, i) => i === index ? { ...u, [field]: value } : { ...u });
      if (field === 'child_count') {
        return recalculatePrices(copy, index);
      }
      return copy;
    });
  };

  const handlePriceChange = (index, field, value) => {
    setUnits((prev) => {
      const copy = prev.map((u, i) => i === index ? { ...u, [field]: value } : { ...u });
      if (index === 0) {
        // When top-level price changes, auto calculate child levels if child_count is present
        return recalculatePrices(copy, 0);
      }
      return copy;
    });
  };

  const setPreset = (presetType) => {
    const defaultBase = form.formulation === 'Capsule' ? 'Capsule' :
                        form.formulation === 'Syrup' || form.formulation === 'Suspension' ? 'Bottle' :
                        form.formulation === 'Injection' ? 'Ampoule' :
                        form.formulation === 'Cream' || form.formulation === 'Ointment' ? 'Tube' : 'Tablet';

    if (presetType === '3-tier') {
      setUnits([
        { unit_name: 'Box', child_count: '', cost_price: '', selling_price: '' },
        { unit_name: 'Strip', child_count: '', cost_price: '', selling_price: '' },
        { unit_name: defaultBase, child_count: 1, cost_price: '', selling_price: '' },
      ]);
    } else if (presetType === '2-tier') {
      setUnits([
        { unit_name: 'Box', child_count: '', cost_price: '', selling_price: '' },
        { unit_name: defaultBase, child_count: 1, cost_price: '', selling_price: '' },
      ]);
    } else if (presetType === 'single') {
      setUnits([
        { unit_name: form.formulation === 'Syrup' || form.formulation === 'Suspension' ? 'Bottle' : form.formulation === 'Cream' ? 'Tube' : 'Piece', child_count: 1, cost_price: '', selling_price: '' },
      ]);
    }
  };

  const addLevel = () => {
    setUnits((prev) => {
      const newUnit = {
        unit_name: 'Piece',
        child_count: 1,
        cost_price: '',
        selling_price: '',
      };
      return [...prev, newUnit];
    });
  };

  const removeLevel = (index) => {
    if (units.length <= 1) {
      toast.error('Medicine must have at least one unit level.');
      return;
    }
    setUnits((prev) => {
      const copy = prev.filter((_, i) => i !== index);
      if (copy.length > 0) {
        copy[copy.length - 1].child_count = 1;
      }
      return copy;
    });
  };

  // Compute total base unit multiplier for top unit
  const getConversionSummary = () => {
    if (units.length === 1) {
      return `Single unit tracking (${units[0].unit_name || 'Unit'})`;
    }
    let factor = 1;
    let hasAllRatios = true;
    for (let i = 0; i < units.length - 1; i++) {
      const c = parseInt(units[i].child_count, 10);
      if (c && c > 0) {
        factor *= c;
      } else {
        hasAllRatios = false;
      }
    }
    const topUnit = units[0].unit_name || 'Box';
    const baseUnit = units[units.length - 1].unit_name || 'Tablet';

    if (!hasAllRatios) {
      return `Define unit packaging ratios below (e.g. 1 ${topUnit} = N ${baseUnit}s)`;
    }

    const parts = units.map((u, i) => {
      if (i === 0) return `1 ${u.unit_name || 'Box'}`;
      let subFactor = 1;
      for (let j = 0; j < i; j++) {
        subFactor *= Math.max(1, parseInt(units[j].child_count, 10) || 1);
      }
      return `${subFactor} ${u.unit_name || 'Unit'}s`;
    });

    return `1 ${topUnit} = ${parts.slice(1).join(' = ')} (${factor} ${baseUnit}s total)`;
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error('Medicine name is required.');
      return;
    }

    if (units.length === 0 || !units[0].unit_name.trim()) {
      toast.error('Please configure at least one unit level.');
      return;
    }

    if (!isEdit && (!form.quantity || parseInt(form.quantity, 10) <= 0)) {
      toast.error('Initial quantity must be greater than zero.');
      return;
    }

    if (form.mf_date && form.expiry_date && form.expiry_date < form.mf_date) {
      toast.error('Expiry date cannot be before manufacture date.');
      return;
    }

    try {
      let categoryId = form.category_id ? parseInt(form.category_id, 10) : null;

      if (!categoryId && form.new_category.trim()) {
        const created = await api.post('/categories', { name: form.new_category.trim(), description: '' });
        categoryId = created?.id ? parseInt(created.id, 10) : null;
      }

      const topUnit = units[0];
      const baseUnit = units[units.length - 1];

      const formattedUnits = units.map((u, idx) => ({
        unit_name: u.unit_name.trim() || `Level ${idx + 1}`,
        child_count: idx === units.length - 1 ? 1 : Math.max(1, parseInt(u.child_count, 10) || 1),
        cost_price: Math.max(0, parseFloat(u.cost_price) || 0),
        selling_price: Math.max(0, parseFloat(u.selling_price) || 0),
        level: idx + 1,
        is_base_unit: idx === units.length - 1 ? 1 : 0,
      }));

      // Batch quantities are stored in the base unit. Packaging ratios may be
      // corrected safely because they only change the display breakdown. The
      // actual base unit, however, cannot change while batch stock exists.
      if (isEdit && editingBatch) {
        const previousBaseUnit = (editingBatch.baseUnitName || '').toLocaleLowerCase();
        const nextBaseUnit = (baseUnit.unit_name || '').trim().toLocaleLowerCase();
        if (previousBaseUnit && nextBaseUnit !== previousBaseUnit) {
          toast.error(`You cannot change the base unit (${editingBatch.baseUnitName}) while this medicine has stock.`);
          return;
        }
      }

      const payload = {
        name: form.name.trim(),
        generic_name: form.generic_name.trim() || null,
        category_id: categoryId,
        supplier_id: null,
        manufacturer: form.manufacturer.trim() || null,
        barcode: form.barcode.trim() || null,
        sku: form.sku.trim() || null,
        selling_price: Math.max(0, parseFloat(topUnit.selling_price) || 0),
        unit: baseUnit.unit_name.trim() || 'pcs',
        reorder_level: Math.max(0, parseInt(form.reorder_level, 10) || 0),
        formulation: form.formulation,
        strength: form.strength.trim() || null,
        pack_size: form.packSize || null,
        description: form.description.trim() || null,
        units: formattedUnits,
      };

      if (isEdit) {
        await api.put(`/medicines/${medicineId}`, payload);
        if (editingBatch) {
          await api.put(`/batches/${editingBatch.id}`, {
            // The edit form accepts full top packages. Keep any loose base
            // units (for example 2 strips remaining after a sale) intact.
            quantity: (Math.max(0, parseInt(form.quantity, 10) || 0) * editingBatch.conversionFactor)
              + (editingBatch.remainderBase || 0),
            mf_date: form.mf_date || null,
            expiry_date: form.expiry_date || null,
          });
        } else if (parseInt(form.quantity, 10) > 0) {
          // Imported medicines have no opening batch. This form still accepts
          // complete top-level packs (for example Boxes), exactly like a new
          // medicine; BatchesController converts them to base units.
          const conversionFactor = formattedUnits.slice(0, -1).reduce(
            (factor, unit) => factor * Math.max(1, parseInt(unit.child_count, 10) || 1),
            1
          );
          await api.post('/batches', {
            medicine_id: parseInt(medicineId, 10),
            batch_number: `OPEN-${String(medicineId).padStart(6, '0')}`,
            quantity: parseInt(form.quantity, 10),
            conversion_factor: conversionFactor,
            cost_price: Math.max(0, parseFloat(topUnit.cost_price) || 0),
            selling_price: Math.max(0, parseFloat(topUnit.selling_price) || 0),
            mf_date: form.mf_date || null,
            expiry_date: form.expiry_date || null,
          });
        }
      } else {
        await api.post('/medicines', {
          ...payload,
          initial_quantity: parseInt(form.quantity, 10),
          mf_date: form.mf_date || null,
          expiry_date: form.expiry_date || null,
          is_active: 1,
        });
      }

      if (isEdit) {
        // Stay on the edit screen so staff can immediately verify or continue
        // updating the stock batch and unit hierarchy.
        await loadMedicine();
        toast.success('Medicine updated successfully!');
        return;
      }

      setSaved(true);
      toast.success('Medicine saved successfully with unit hierarchy!');
      setTimeout(() => navigate('/medicines'), 1200);
    } catch (err) {
      toast.error(err.message || 'Failed to save medicine');
    }
  };

  if (saved) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100">
          <CheckCircle2 className="h-8 w-8 text-emerald-600" />
        </div>
        <h2 className="text-lg font-bold text-gray-900">Medicine {isEdit ? 'Updated' : 'Saved'} Successfully!</h2>
        <p className="mt-1 text-sm text-gray-500">Redirecting to medicines catalog...</p>
      </div>
    );
  }

  if (loadingMedicine) {
    return <div className="flex items-center justify-center py-20 text-sm font-medium text-gray-500">Loading medicine...</div>;
  }

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/medicines')}
            className="rounded-lg p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-100">
              <Pill className="h-5 w-5 text-emerald-600" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">{isEdit ? 'Edit Medicine' : 'New Medicine'}</h1>
              <p className="text-xs text-gray-500">{isEdit ? 'Update medicine details, packaging & unit conversions' : 'Add medicine with dynamic packaging & unit conversions'}</p>
            </div>
          </div>
        </div>
        <button
          onClick={() => navigate('/medicines')}
          className="text-sm font-medium text-gray-500 transition-colors hover:text-gray-700"
        >
          &larr; Discard & Go Back
        </button>
      </div>

      <form onSubmit={handleSave} className="space-y-6">
        {/* ── Basic Identification ── */}
        <SectionCard title="Basic Identification">
          <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
            <InputField
              label="Barcode / SKU"
              value={form.barcode}
              onChange={updateForm('barcode')}
              placeholder="Scan or type..."
              icon={ScanBarcode}
            />
            <div className="md:col-span-2">
              <InputField
                label="Medicine Name"
                value={form.name}
                onChange={updateForm('name')}
                placeholder="e.g. Panadol Extra 500mg"
                required
              />
            </div>
            <InputField
              label="Generic Name (Formula)"
              value={form.generic_name}
              onChange={updateForm('generic_name')}
              placeholder="e.g. Paracetamol + Caffeine"
            />
            <InputField
              label="Manufacturer / Brand"
              value={form.manufacturer}
              onChange={updateForm('manufacturer')}
              placeholder="e.g. GSK Pakistan"
            />
            <div>
              <label className="mb-1.5 block text-[11px] font-bold uppercase tracking-wider text-gray-500">Inventory Category</label>
              {categories.length > 0 && (
                <SearchableSelect
                  value={form.category_id}
                  onChange={(val) => setForm({ ...form, category_id: val, new_category: '' })}
                  options={categories.map((c) => ({ value: String(c.id), label: c.name }))}
                  placeholder="-- Select Category --"
                  searchPlaceholder="Search category..."
                  size="lg"
                />
              )}
              <input
                value={form.new_category}
                onChange={(e) => setForm({ ...form, new_category: e.target.value, category_id: '' })}
                placeholder={categories.length > 0 ? 'Or type new category...' : 'Type category name...'}
                className="w-full mt-1.5 rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-900 placeholder-gray-400 transition-all focus:border-emerald-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
              />
            </div>
          </div>
        </SectionCard>

        {/* ── Clinical Specifications ── */}
        <SectionCard title="Clinical Specifications" icon={Stethoscope}>
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-3">
            <SelectField
              label="Formulation"
              value={form.formulation}
              onChange={handleFormulationChange}
              options={formulations}
              creatable={true}
              searchPlaceholder="Search or type formulation..."
              placeholder="Select or add formulation..."
            />
            <InputField
              label="Strength"
              value={form.strength}
              onChange={updateForm('strength')}
              placeholder="e.g. 500mg / 5ml"
            />
            <InputField
              label="Low Stock Threshold (Base Units)"
              value={form.reorder_level}
              onChange={updateForm('reorder_level')}
              placeholder="10"
              type="number"
              suffix="UNITS"
            />
          </div>
        </SectionCard>

        <SectionCard title={isEdit ? 'Current Stock Batch' : 'Initial Inventory'} icon={Layers}>
          <p className="mb-5 text-xs text-gray-500">
            {isEdit
              ? (editingBatch
                ? `Editing current batch: ${editingBatch.batchNumber || 'Unnamed batch'}. Stored safely in base units: ${stockBreakdown}.`
                : `No active stock batch is available. Enter complete ${units[0]?.unit_name || 'top-unit'} packs; they will be saved in base units: ${stockBreakdown}.`)
              : 'Add the opening quantity and product dates while creating this medicine.'}
          </p>
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-3">
            <InputField
              label={isEdit ? `Full ${units[0]?.unit_name || 'Top Unit'} Quantity` : 'Quantity'}
              value={form.quantity}
              onChange={updateForm('quantity')}
              placeholder="e.g. 10"
              type="number"
              required={!isEdit}
              suffix={isEdit ? (units[0]?.unit_name || 'TOP UNIT').toUpperCase() : 'TOP UNIT'}
            />
            <InputField
              label="Manufacture Date"
              value={form.mf_date}
              onChange={updateForm('mf_date')}
              type="date"
            />
            <InputField
              label="Expiry Date"
              value={form.expiry_date}
              onChange={updateForm('expiry_date')}
              type="date"
            />
          </div>
        </SectionCard>

        {/* ── Dynamic Multi-Level Unit Conversion & Pricing Hierarchy ── */}
        <SectionCard title="Unit Conversion & Multi-Level Pricing" icon={Layers}>
          <div className="space-y-4">
            {/* Quick Presets Bar */}
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-emerald-100 bg-emerald-50/50 p-3">
              <div className="flex items-center gap-2 text-xs font-semibold text-emerald-900">
                <Layers className="h-4 w-4 text-emerald-600" />
                <span>Packaging Presets:</span>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={() => setPreset('3-tier')}
                  className="rounded-lg border border-emerald-300 bg-white px-3 py-1.5 text-xs font-bold text-emerald-800 shadow-xs hover:bg-emerald-100 transition-colors"
                >
                  📦 3-Tier: Box → Strip → Tablet
                </button>
                <button
                  type="button"
                  onClick={() => setPreset('2-tier')}
                  className="rounded-lg border border-emerald-300 bg-white px-3 py-1.5 text-xs font-bold text-emerald-800 shadow-xs hover:bg-emerald-100 transition-colors"
                >
                  📦 2-Tier: Box → Tablet
                </button>
                <button
                  type="button"
                  onClick={() => setPreset('single')}
                  className="rounded-lg border border-emerald-300 bg-white px-3 py-1.5 text-xs font-bold text-emerald-800 shadow-xs hover:bg-emerald-100 transition-colors"
                >
                  💊 Single Unit (Bottle/Piece)
                </button>
              </div>
            </div>

            {/* Formula Summary Box */}
            <div className="rounded-xl border border-gray-200 bg-gray-50/80 p-3 flex items-center justify-between text-xs">
              <div className="flex items-center gap-2 text-gray-700 font-semibold">
                <Info className="h-4 w-4 text-emerald-600 flex-shrink-0" />
                <span>Conversion: <strong className="text-emerald-700">{getConversionSummary()}</strong></span>
              </div>
              <button
                type="button"
                onClick={() => setUnits((prev) => recalculatePrices(prev, 0))}
                className="flex items-center gap-1 text-[11px] font-bold text-emerald-600 hover:text-emerald-800 hover:underline"
                title="Auto-calculate child unit prices proportionally from top level"
              >
                <RefreshCw className="h-3 w-3" />
                Recalculate Proportions
              </button>
            </div>

            {/* Unit Hierarchy Table */}
            <div className="overflow-visible rounded-xl border border-gray-200">
              <table className="w-full text-left text-xs border-collapse">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50 font-bold uppercase tracking-wider text-gray-600 text-[10px]">
                    <th className="py-2.5 px-3 w-16 text-center">Level</th>
                    <th className="py-2.5 px-3 min-w-[170px]">Unit Name</th>
                    <th className="py-2.5 px-3 min-w-[160px]">Contains Next Unit</th>
                    <th className="py-2.5 px-3 min-w-[130px]">Cost Price (Rs.)</th>
                    <th className="py-2.5 px-3 min-w-[130px]">Selling Price (Rs.)</th>
                    <th className="py-2.5 px-2 w-10 text-center"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 bg-white">
                  {units.map((unit, idx) => {
                    const isTop = idx === 0;
                    const isBase = idx === units.length - 1;
                    const nextUnitName = !isBase ? units[idx + 1].unit_name || 'items' : '';

                    return (
                      <tr key={idx} className="hover:bg-gray-50/60 transition-colors">
                        {/* Level badge */}
                        <td className="py-3 px-3 text-center">
                          <span
                            className={clsx(
                              'inline-flex h-6 w-6 items-center justify-center rounded-full text-xs font-bold',
                              isTop
                                ? 'bg-emerald-100 text-emerald-800 border border-emerald-300'
                                : isBase
                                ? 'bg-blue-100 text-blue-800 border border-blue-300'
                                : 'bg-gray-100 text-gray-700'
                            )}
                          >
                            {idx + 1}
                          </span>
                        </td>

                        {/* Unit Name (using platform SearchableSelect with creatable) */}
                        <td className="py-3 px-3">
                          <SearchableSelect
                            value={unit.unit_name}
                            onChange={(val) => handleUnitChange(idx, 'unit_name', val)}
                            options={COMMON_UNIT_NAMES}
                            placeholder={isTop ? 'e.g. Box' : isBase ? 'e.g. Tablet' : 'e.g. Strip'}
                            searchPlaceholder="Search or type unit..."
                            creatable={true}
                            size="sm"
                            buttonClassName="bg-white border-gray-200 py-1.5 text-xs font-semibold"
                          />
                          <p className="mt-0.5 text-[10px] text-gray-400">
                            {isTop ? '📦 Top Unit (Stock Inflow)' : isBase ? '💊 Base Unit (Lowest Stock)' : 'Sub-unit'}
                          </p>
                        </td>

                        {/* Ratio / Contains Next */}
                        <td className="py-3 px-3">
                          {!isBase ? (
                            <div className="flex items-center gap-1.5">
                              <span className="text-gray-500 font-medium whitespace-nowrap">1 {unit.unit_name || 'Unit'} =</span>
                              <input
                                type="number"
                                min="1"
                                placeholder="Qty"
                                value={unit.child_count}
                                onChange={(e) => handleUnitChange(idx, 'child_count', e.target.value)}
                                className="w-16 rounded-lg border border-gray-200 bg-gray-50 px-2 py-1.5 text-center text-xs font-bold text-gray-900 focus:border-emerald-500 focus:bg-white focus:outline-none"
                              />
                              <span className="font-semibold text-gray-700 truncate">{nextUnitName}s</span>
                            </div>
                          ) : (
                            <span className="inline-flex items-center rounded-md bg-blue-50 px-2.5 py-1 text-[11px] font-bold text-blue-700 border border-blue-200">
                              Base Unit (Multiplier: 1)
                            </span>
                          )}
                        </td>

                        {/* Cost Price */}
                        <td className="py-3 px-3">
                          <div className="relative">
                            <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[11px] text-gray-400 font-medium">Rs.</span>
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              value={unit.cost_price}
                              onChange={(e) => handlePriceChange(idx, 'cost_price', e.target.value)}
                              placeholder="0.00"
                              className="w-full rounded-lg border border-gray-200 bg-gray-50 pl-8 pr-2 py-1.5 text-xs font-semibold text-gray-900 focus:border-emerald-500 focus:bg-white focus:outline-none"
                            />
                          </div>
                        </td>

                        {/* Selling Price */}
                        <td className="py-3 px-3">
                          <div className="relative">
                            <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[11px] text-gray-400 font-medium">Rs.</span>
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              value={unit.selling_price}
                              onChange={(e) => handlePriceChange(idx, 'selling_price', e.target.value)}
                              placeholder="0.00"
                              className="w-full rounded-lg border border-emerald-300 bg-emerald-50/40 pl-8 pr-2 py-1.5 text-xs font-bold text-emerald-950 focus:border-emerald-500 focus:bg-white focus:outline-none"
                              required
                            />
                          </div>
                        </td>

                        {/* Remove Action */}
                        <td className="py-3 px-2 text-center">
                          {units.length > 1 && (
                            <button
                              type="button"
                              onClick={() => removeLevel(idx)}
                              className="rounded-lg p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-600 transition-colors"
                              title="Remove level"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Add Level Button */}
            <div className="flex justify-end pt-1">
              <button
                type="button"
                onClick={addLevel}
                className="flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 shadow-xs hover:bg-gray-50 transition-colors"
              >
                <Plus className="h-3.5 w-3.5" />
                Add Sub-level
              </button>
            </div>
          </div>
        </SectionCard>

        {/* ── Description ── */}
        <SectionCard title="Additional Notes" icon={FileText}>
          <textarea
            value={form.description}
            onChange={updateForm('description')}
            placeholder="Any additional notes or instructions about this medicine..."
            rows={3}
            className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-900 placeholder-gray-400 transition-all focus:border-emerald-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
          />
        </SectionCard>

        {/* ── Actions ── */}
        <div className="flex items-center justify-end gap-3 rounded-2xl border border-gray-200 bg-white px-6 py-4 shadow-sm">
          <button
            type="button"
            onClick={() => navigate('/medicines')}
            className="rounded-xl border border-gray-300 bg-white px-6 py-2.5 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="flex items-center gap-2 rounded-xl bg-emerald-500 px-6 py-2.5 text-sm font-semibold text-white shadow-sm transition-all hover:bg-emerald-600 active:scale-[0.97]"
          >
            <Save className="h-4 w-4" />
            {isEdit ? 'Update Medicine & Units' : 'Save Medicine & Units'}
          </button>
        </div>
      </form>
    </div>
  );
}
